import React, { useCallback, useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { FeatureIcon } from '@rossum/ui';
import { IconBrowser, IconTestPipe, IconX } from '@rossum/ui/icons/tabler';
import {
  Button,
  Chip,
  Divider,
  Grow,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  SvgIcon,
  Switch,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { getExpectedErrorKey } from '../../lib/errorHandlers';
import { updateUiSettings } from '../../redux/modules/user/actions';
import { userSelector } from '../../redux/modules/user/selectors';
import {
  isFeatureGenerallyAvailable,
  isFeatureLocallyOverridden,
  isGaDateVisible,
} from '../feature-enablement/featureEnablement';
import {
  Feature,
  FeatureId,
  isLabFeature,
} from '../feature-enablement/features';
import { useCreateSurvey } from '../surveys/hooks/useCreateSurvey';
import { usePatchSurvey } from '../surveys/hooks/usePatchSurvey';
import { surveysUuidMap } from '../surveys/surveysUuidMap';
import { notify } from '../toaster';
import { FeatureFeedbackCollector } from './FeatureFeedbackCollector';

type FeatureConfig = {
  enabled: boolean;
  askForFeedback?: boolean;
  displayAddControl?: boolean;
  externalFlagValue?: boolean;
};

type FeatureTogglesProps = {
  features: Feature[];
  config: Partial<Record<FeatureId, FeatureConfig>>;
  readonly?: boolean;
  onChange?: (featureId: FeatureId, value: boolean | null) => void;
  clearable?: boolean;
  collectFeedback?: boolean;
  onFeedbackSubmit?: (featureId: FeatureId, feedback: string) => void;
  onFeedbackReject?: (featureId: FeatureId) => void;
  showOverridesWarnings?: boolean;
};

const sortAlphabetically = <T extends Array<{ id: string; name?: string }>>(
  features: T
) =>
  R.pipe(
    features,
    R.sortBy(feature => feature.name?.toLowerCase() ?? feature.id.toLowerCase())
  );

export const FeatureToggles = ({
  features,
  config,
  readonly = false,
  onChange,
  clearable = false,
  collectFeedback = false,
  showOverridesWarnings = true,
}: FeatureTogglesProps) => {
  const intl = useIntl();

  const dispatch = useDispatch();
  const user = useSelector(userSelector);

  const { mutateAsync: createSurvey } = useCreateSurvey(
    surveysUuidMap.featureFeedbackSurvey
  );

  const { mutateAsync: updateSurvey } = usePatchSurvey();

  const [showFeedbackForFeature, setShowFeedbackForFeature] =
    useState<FeatureId | null>(null);

  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);

  const setFeedbackSent = useCallback(
    (featureId: FeatureId) => {
      dispatch(
        updateUiSettings({
          labConfig: {
            [featureId]: {
              sentFeedback: true,
            },
          },
        })
      );
    },
    [dispatch]
  );

  const onFeatureFeedbackSubmit = useCallback(
    (featureId: FeatureId, feedback: string) => {
      if (feedback.length) {
        createSurvey()
          .then(survey => {
            updateSurvey({
              surveyId: survey.id,
              surveyPatchModel: {
                answers: [
                  {
                    value: feedback,
                  },
                ],
                additionalData: {
                  featureId,
                },
              },
            })
              .then(() => {
                setFeedbackSent(featureId);
                setShowFeedbackPrompt(false);
                setShowFeedbackForFeature(null);
                notify.default({
                  title: intl.formatMessage({
                    id: 'features.featureEnablement.feedback.toast.success',
                  }),
                });
              })
              .catch(e => {
                const expectedError = getExpectedErrorKey({
                  error: e,
                  expectedError: {
                    key: 'size-limit' as const,
                    value: 'Ensure this field has no more than 250 characters',
                  },
                });
                const translatedError = expectedError
                  ? {
                      title: intl.formatMessage({
                        id: 'features.featureEnablement.feedback.toast.sizeLimitError.title',
                      }),
                      description: intl.formatMessage({
                        id: 'features.featureEnablement.feedback.toast.sizeLimitError.description',
                      }),
                    }
                  : {
                      title: intl.formatMessage({
                        id: 'features.featureEnablement.feedback.toast.error',
                      }),
                    };
                notify.error(translatedError);
              });
          })
          .catch(() => {
            notify.error({
              title: intl.formatMessage({
                id: 'features.featureEnablement.feedback.toast.error',
              }),
            });
          });
      }
    },
    [createSurvey, updateSurvey, setFeedbackSent, intl]
  );

  const onFeatureFeedbackReject = useCallback(
    (featureId: FeatureId) => {
      setFeedbackSent(featureId);
      setShowFeedbackPrompt(false);
      setShowFeedbackForFeature(null);
    },
    [setFeedbackSent]
  );

  const handleToggle = useCallback(
    (flagName: FeatureId, enabled: boolean | null) => {
      if (readonly) return;

      if (onChange) {
        onChange(flagName, enabled);
      } else {
        const userLabConfig = user.uiSettings?.labConfig || {};
        dispatch(
          updateUiSettings({
            labConfig:
              enabled === null
                ? undefined
                : {
                    ...userLabConfig,
                    [flagName]: {
                      enabled,
                      lastUpdatedAt: new Date().toISOString(),
                    },
                  },
          })
        );
      }

      if (collectFeedback && !enabled && !!config[flagName]?.askForFeedback) {
        setShowFeedbackForFeature(flagName);
        setShowFeedbackPrompt(true);
      }
    },
    [
      readonly,
      onChange,
      collectFeedback,
      config,
      user.uiSettings?.labConfig,
      dispatch,
    ]
  );

  const sortedFeatures = useMemo(() => {
    const [featuresWithAddControl, featuresWithoutAddControl] = R.partition(
      features,
      feature => !!config[feature.id]?.displayAddControl
    );

    return [
      ...sortAlphabetically(featuresWithoutAddControl),
      ...sortAlphabetically(featuresWithAddControl),
    ];
  }, [features, config]);

  return (
    <Stack sx={{ height: '100%', width: '100%' }} spacing={2}>
      <List disablePadding dense sx={{ width: '100%' }}>
        {sortedFeatures.map(feature => {
          const { enabled, displayAddControl, externalFlagValue } = config[
            feature.id
          ] ?? {
            enabled: false,
            displayAddControl: false,
            externalFlagValue: undefined,
          };

          const gaDateLabel =
            isLabFeature(feature) && feature.gaDate
              ? intl.formatDate(new Date(feature.gaDate), {
                  dateStyle: 'medium',
                })
              : null;

          return (
            <React.Fragment key={feature.id}>
              <Divider />
              <ListItem
                sx={{
                  gap: 3,
                  px: 4,
                  py: 2,
                  alignItems: 'center',
                  borderLeft: '2px solid',
                  borderLeftColor: theme =>
                    showOverridesWarnings &&
                    !readonly &&
                    isFeatureLocallyOverridden(feature.id)
                      ? theme.palette.warning.main
                      : 'transparent',
                  // TODO: This doesn't work with listItemClasses for some reason, likely MUI bug
                  [`& .MuiListItemSecondaryAction-root`]: {
                    alignSelf: 'flex-start',
                    position: 'relative',
                    transform: 'none',
                    top: 0,
                  },
                }}
                secondaryAction={
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                      alignSelf: 'flex-start',
                      top: 0,
                    }}
                  >
                    {collectFeedback && (
                      <Tooltip
                        title={intl.formatMessage({
                          id: isFeatureLocallyOverridden(feature.id)
                            ? 'components.featureToggles.removeOverride'
                            : 'components.featureToggles.giveFeedback',
                        })}
                        enterDelay={500}
                      >
                        <span>
                          <Button
                            color="secondary"
                            onClick={() =>
                              setShowFeedbackForFeature(currentFeatureId =>
                                currentFeatureId === feature.id
                                  ? null
                                  : feature.id
                              )
                            }
                            data-cy={`feature-toggle-feedback-button-${feature.id}`}
                            disabled={isFeatureLocallyOverridden(feature.id)}
                            variant="outlined"
                          >
                            {intl.formatMessage({
                              id: 'components.featureToggles.feedback',
                            })}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {!displayAddControl && (
                      <Switch
                        checked={enabled}
                        onChange={() => handleToggle(feature.id, !enabled)}
                        disabled={readonly}
                        slotProps={{
                          input: {
                            // @ts-expect-error
                            'data-cy': `feature-toggle-switch-${feature.id}`,
                          },
                        }}
                        aria-label={
                          enabled
                            ? intl.formatMessage({
                                id: 'components.featureToggles.disableFeature',
                              })
                            : intl.formatMessage({
                                id: 'components.featureToggles.enableFeature',
                              })
                        }
                      />
                    )}
                    {!readonly && clearable && !displayAddControl ? (
                      <IconButton
                        edge="end"
                        onClick={() =>
                          !readonly && handleToggle(feature.id, null)
                        }
                        disabled={readonly}
                        size="small"
                        data-cy={`feature-toggle-clear-${feature.id}`}
                      >
                        <SvgIcon color="action" fontSize="medium">
                          <IconX />
                        </SvgIcon>
                      </IconButton>
                    ) : null}

                    {displayAddControl ? (
                      <Button
                        onClick={() => handleToggle(feature.id, true)}
                        data-cy={`feature-toggle-add-${feature.id}`}
                      >
                        {intl.formatMessage({
                          id: 'components.featureToggles.buttons.addToStorage',
                        })}
                      </Button>
                    ) : null}
                  </Stack>
                }
              >
                <ListItemIcon
                  sx={{ minWidth: 40, alignSelf: 'flex-start', pt: 1 }}
                >
                  <FeatureIcon
                    color="secondary"
                    content={
                      feature.icon ??
                      (feature.type === 'lab' ? IconTestPipe : IconBrowser)
                    }
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      useFlexGap
                    >
                      <Typography variant="body2" fontWeight="bold" noWrap>
                        {isLabFeature(feature) ? feature.name : feature.id}
                      </Typography>
                      {!readonly &&
                      isFeatureLocallyOverridden(feature.id) &&
                      showOverridesWarnings ? (
                        <Tooltip
                          title={intl.formatMessage({
                            id: 'components.featureToggles.featureHasLocalOverrides',
                          })}
                        >
                          <Chip
                            label={intl.formatMessage({
                              id: 'components.featureToggles.enableFeature.chips.warning',
                            })}
                            size="small"
                            color="warning"
                            data-cy={`feature-toggle-warning-${feature.id}`}
                          />
                        </Tooltip>
                      ) : null}
                      {externalFlagValue ? (
                        <Chip
                          color="success"
                          size="small"
                          label={intl.formatMessage({
                            id: 'components.featureToggles.enableFeature.chips.enabled',
                          })}
                        />
                      ) : null}
                      {isLabFeature(feature) &&
                      feature.gaDate &&
                      isGaDateVisible(feature.gaDate) ? (
                        <Tooltip
                          title={
                            isFeatureGenerallyAvailable(feature.id)
                              ? intl.formatMessage({
                                  id: 'components.featureToggles.featureGA',
                                })
                              : intl.formatMessage(
                                  {
                                    id: 'components.featureToggles.featureNotGA',
                                  },
                                  {
                                    date: gaDateLabel,
                                  }
                                )
                          }
                        >
                          <Chip
                            label={gaDateLabel}
                            size="small"
                            color={
                              isFeatureGenerallyAvailable(feature.id)
                                ? 'success'
                                : 'default'
                            }
                            sx={{
                              cursor: 'default',
                            }}
                            variant="filled"
                          />
                        </Tooltip>
                      ) : null}
                    </Stack>
                  }
                  secondary={feature.description || null}
                />
              </ListItem>
              <Grow
                in={
                  collectFeedback &&
                  showFeedbackForFeature === feature.id &&
                  !isFeatureLocallyOverridden(feature.id)
                }
                appear
                unmountOnExit
              >
                <Stack sx={{ px: 4 }}>
                  <FeatureFeedbackCollector
                    featureId={feature.id}
                    onFeedbackSubmit={onFeatureFeedbackSubmit}
                    onFeedbackReject={onFeatureFeedbackReject}
                    showPrompt={showFeedbackPrompt}
                  />
                </Stack>
              </Grow>
            </React.Fragment>
          );
        })}
      </List>
    </Stack>
  );
};
